import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Tooltip, Typography, useTheme } from '@mui/material'
import { styled } from '@mui/material/styles'
import { format } from 'date-fns'
import { navigate } from 'gatsby'
import { useLocation } from '@gatsbyjs/reach-router'
import LighthouseIcon from '../../assets/images/icons/lighthouse.svg'
import PdfIcon from '../../images/pdf.svg'
import { USER_EXPERIENCES } from '../../utils/constants'
import { useToken } from '../../utils/context/TokenContext'
import { useAuthContext } from '../../utils/context/AuthContext'
import { useCookies } from 'react-cookie'

const FlexContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
}))

const PdfIconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: 52,
  width: 52,
  marginRight: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    marginLeft: theme.spacing(-1),
  },
}))

const Title = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isHeader',
})(({ theme, isHeader }) => ({
  color: '#092A48',
  fontWeight: isHeader ? 'bold' : 500,
  fontSize: isHeader
    ? theme.typography.h6.fontSize
    : theme.typography.body2.fontSize,
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.body2.fontSize,
  },
}))

const DateText = styled(Typography)(({ theme }) => ({
  color: '#B9B9B9',
  fontSize: 16,
  fontWeight: 400,
  lineHeight: '30px',
  marginRight: theme.spacing(1),
}))

const LighthouseImage = styled('img')(() => ({
  marginLeft: 10,
}))

const GridItemContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  width: '100%',
  boxSizing: 'border-box',
  padding: theme.spacing(1),
}))

const linkStyle = { textDecoration: 'none', color: 'inherit' }

const formatDateFn = (date) => format(new Date(date), 'MMM dd, yyyy')

const Container = ({ institutionalOnly, children, publicURL, onClick, assetURL, ...rest }) => {
  const [cookies] = useCookies(['userExperience'])
  const isInstitutional = cookies?.userExperience === USER_EXPERIENCES.INSTITUTIONAL_ONLY
  const { pathname, state } = useLocation()
  const { isAuthVerified, verifyAuthToken } = useToken()
  const auth = useAuthContext()
  const currentUserExperience = cookies?.userExperience || auth?.state?.userExperience  
  const handleClick = useCallback(
    async (event) => {
      if (
        institutionalOnly &&
        (!auth?.state?.isLoggedIn ||
          currentUserExperience !== USER_EXPERIENCES.INSTITUTIONAL_ONLY)
      ) {
        event.preventDefault()
        if (
          isInstitutional ||
          (isAuthVerified && typeof window !== 'undefined')
        ) {
          window.open(assetURL, '_blank')
        } else {
          try {
            const verified = await verifyAuthToken()
            if (verified && typeof window !== 'undefined') {
              window.open(assetURL, '_blank')
            } else {
              navigate(`${pathname}?form=login`, {
                replace: true,
                state: {
                  data: {
                    from: pathname,
                    document: assetURL,
                  },
                },
              })
            }
          } catch (error) {
            console.error('Error verifying auth token:', error)
          }
        }
      }
    },
    [
      onClick,
      auth?.state,
      institutionalOnly,
      isAuthVerified,
      publicURL,
      pathname,
      verifyAuthToken,
    ]
  )

  return (
    <a
      href={isInstitutional || isAuthVerified ? assetURL : publicURL}
      onClick={onClick ? onClick : handleClick}
      style={linkStyle}
      {...rest}
    >
      {children}
    </a>
  )
}

Container.propTypes = {
  institutionalOnly: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  publicURL: PropTypes.string,
  assetURL: PropTypes.string,
  ariaLabel: PropTypes.string,
  ariaLabelledby: PropTypes.string,
}

const GridItem = ({
  checkUserExperience,
  description,
  publicURL,
  updatedAt = '',
  userExperience,
  header = false,
  className = '',
  withoutIcon = false,
  noInstitutionalLogo = false,
  ariaLabel = '',
  ariaLabelledby = '',
  assetURL,
}) => {
  const [institutionalOnly, setInstitutionalOnly] = useState(false)
  const [showStaticImage, setShowStaticImage] = useState(false)
  const theme = useTheme()

  useEffect(() => {
    const uxInstitutionalOnly =
      userExperience === USER_EXPERIENCES.INSTITUTIONAL_ONLY
    setInstitutionalOnly(uxInstitutionalOnly)
    setShowStaticImage(checkUserExperience && uxInstitutionalOnly)
  }, [checkUserExperience, userExperience])

  const tooltipProps = useMemo(
    () => ({
      title: 'Institutional investors only: Login required',
      placement: 'left',
    }),
    []
  )

  return (
    <GridItemContainer className={className}>
      <Container
        institutionalOnly={institutionalOnly}
        publicURL={publicURL}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        assetURL={assetURL}
      >
        <FlexContainer>
          {!header && !withoutIcon && (
            <PdfIconWrapper>
              <img src={PdfIcon} alt="PDF Icon" width={52} height={52} />
            </PdfIconWrapper>
          )}
          <Box>
            <Title isHeader={header}>{description}</Title>
            <Box display="flex" alignItems="center">
              {updatedAt ? (
                <DateText>{formatDateFn(updatedAt)}</DateText>
              ) : (
                <Box width={0} height={0} />
              )}
              {institutionalOnly && !noInstitutionalLogo && showStaticImage && (
                <Tooltip {...tooltipProps}>
                  <LighthouseImage
                    src={LighthouseIcon}
                    alt="Institutional investors only: Login required"
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        </FlexContainer>
      </Container>
    </GridItemContainer>
  )
}

GridItem.propTypes = {
  checkUserExperience: PropTypes.bool.isRequired,
  description: PropTypes.string.isRequired,
  publicURL: PropTypes.string,
  assetURL: PropTypes.string,
  updatedAt: PropTypes.string,
  userExperience: PropTypes.string,
  header: PropTypes.bool,
  className: PropTypes.string,
  withoutIcon: PropTypes.bool,
  noInstitutionalLogo: PropTypes.bool,
  ariaLabel: PropTypes.string,
  ariaLabelledby: PropTypes.string,
  onClick: PropTypes.func,
}

export default GridItem
